// src/features/kommunikation/services/DocumentStorageService.ts

import { supabase } from '../../../shared/services/supabase';
import { authService } from '../../../shared/services/auth.service';

interface StorageUploadResult {
  success: boolean;
  storagePath?: string;
  error?: string;
}

interface StorageDownloadResult {
  success: boolean;
  downloadUrl?: string;
  error?: string;
}

/**
 * Service für Supabase Storage Operationen (communication-documents bucket)
 */
export class DocumentStorageService {
  private readonly BUCKET_NAME = 'communication-documents';
  private readonly MAX_FILE_SIZE_MB = 50;
  
  /**
   * Stellt sicher, dass Supabase authentifiziert ist
   */
  private async ensureAuthenticated(): Promise<boolean> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        return true;
      }
      
      console.log('No Supabase session found, attempting auto-login...');
      const loginSuccess = await authService.autoLogin();
      
      if (!loginSuccess) {
        console.error('Supabase authentication failed for storage access');
        return false;
      }
      
      const { data: { session: newSession } } = await supabase.auth.getSession();
      return !!newSession;
      
    } catch (error) {
      console.error('Authentication check failed:', error);
      return false;
    }
  }
  
  /**
   * Sanitiert einen String für Supabase Storage Pfade
   * Ersetzt Umlaute und Sonderzeichen durch ASCII-kompatible Zeichen
   */
  private sanitizeForStoragePath(input: string): string {
    if (!input) return '';
    
    // Deutsche Umlaute und ß ersetzen (echte Unicode-Zeichen)
    const umlautMap: Record<string, string> = {
      // Deutsche Umlaute (echte Unicode-Zeichen)
      'ä': 'ae',
      'Ä': 'Ae',
      'ö': 'oe',
      'Ö': 'Oe',
      'ü': 'ue',
      'Ü': 'Ue',
      'ß': 'ss',
      // Französische Akzente
      'é': 'e',
      'è': 'e',
      'ê': 'e',
      'ë': 'e',
      'É': 'E',
      'È': 'E',
      'Ê': 'E',
      'Ë': 'E',
      'á': 'a',
      'à': 'a',
      'â': 'a',
      'ã': 'a',
      'Á': 'A',
      'À': 'A',
      'Â': 'A',
      'Ã': 'A',
      'í': 'i',
      'ì': 'i',
      'î': 'i',
      'ï': 'i',
      'Í': 'I',
      'Ì': 'I',
      'Î': 'I',
      'Ï': 'I',
      'ó': 'o',
      'ò': 'o',
      'ô': 'o',
      'õ': 'o',
      'Ó': 'O',
      'Ò': 'O',
      'Ô': 'O',
      'Õ': 'O',
      'ú': 'u',
      'ù': 'u',
      'û': 'u',
      'Ú': 'U',
      'Ù': 'U',
      'Û': 'U',
      'ñ': 'n',
      'Ñ': 'N',
      'ç': 'c',
      'Ç': 'C',
      // Polnische Zeichen
      'ł': 'l',
      'Ł': 'L',
      'ń': 'n',
      'Ń': 'N',
      'ś': 's',
      'Ś': 'S',
      'ź': 'z',
      'Ź': 'Z',
      'ż': 'z',
      'Ż': 'Z',
      'ć': 'c',
      'Ć': 'C',
      'ą': 'a',
      'Ą': 'A',
      'ę': 'e',
      'Ę': 'E',
      // Tschechische Zeichen
      'ř': 'r',
      'Ř': 'R',
      'ě': 'e',
      'Ě': 'E',
      'š': 's',
      'Š': 'S',
      'č': 'c',
      'Č': 'C',
      'ž': 'z',
      'Ž': 'Z',
      'ů': 'u',
      'Ů': 'U',
      'ý': 'y',
      'Ý': 'Y',
      'ď': 'd',
      'Ď': 'D',
      'ť': 't',
      'Ť': 'T',
      'ň': 'n',
      'Ň': 'N',
      // Türkische Zeichen
      'ğ': 'g',
      'Ğ': 'G',
      'ş': 's',
      'Ş': 'S',
      'ı': 'i',
      'İ': 'I',
      // Skandinavische Zeichen
      'å': 'a',
      'Å': 'A',
      'æ': 'ae',
      'Æ': 'Ae',
      'ø': 'o',
      'Ø': 'O',
      // Rumänische Zeichen
      'ă': 'a',
      'Ă': 'A',
      'ș': 's',
      'Ș': 'S',
      'ț': 't',
      'Ț': 'T'
    };
    
    let result = input;
    
    // Ersetze alle bekannten Sonderzeichen
    for (const [char, replacement] of Object.entries(umlautMap)) {
      result = result.split(char).join(replacement);
    }
    
    // Entferne alle verbleibenden nicht-ASCII Zeichen und erlaube nur sichere Zeichen
    // Erlaubt: a-z, A-Z, 0-9, -, _
    result = result.replace(/[^a-zA-Z0-9\-_]/g, '_');
    
    // Mehrfache Unterstriche zusammenfassen
    result = result.replace(/_+/g, '_');
    
    // Führende/abschließende Unterstriche entfernen
    result = result.replace(/^_+|_+$/g, '');
    
    return result || 'Unbekannt';
  }
  
  /**
   * Generiert einen eindeutigen Storage-Pfad mit neuem Dateinamen-Format
   * Format: JJJJMM_Gehaltsunterlagen_Nachname_Vorname.pdf
   * Mit Sanitierung für Umlaute und Sonderzeichen
   */
  private generateStoragePath(
    mandantennummer: string,
    personalnummer: string,
    monat: number,
    jahr: number,
    originalFilename: string
  ): string {
    // Parse Name aus originalFilename
    // Erwartetes Format: {mandant}_{personal}_{Nachname}_{Vorname}_{Monat}_{Jahr}_{Typ}.pdf
    const filenameParts = originalFilename.replace('.pdf', '').split('_');
    
    // Extrahiere Nachname und Vorname (Index 2 und 3) und sanitiere sie
    const nachnameRaw = filenameParts[2] || 'Unbekannt';
    const vornameRaw = filenameParts[3] || '';
    
    const nachname = this.sanitizeForStoragePath(nachnameRaw);
    const vorname = this.sanitizeForStoragePath(vornameRaw);
    
    // Neues Format: JJJJMM_Gehaltsunterlagen_Nachname_Vorname.pdf
    const jjjjmm = `${jahr}${monat.toString().padStart(2, '0')}`;
    
    // Baue Dateiname zusammen (ohne trailing underscore wenn Vorname leer)
    let newFilename = `${jjjjmm}_Gehaltsunterlagen_${nachname}`;
    if (vorname) {
      newFilename += `_${vorname}`;
    }
    newFilename += '.pdf';
    
    // Storage-Pfad: jahr/monat/mandant/personal/filename
    return `${jahr}/${monat.toString().padStart(2, '0')}/${mandantennummer}/${personalnummer}/${newFilename}`;
  }
  
  /**
   * Lädt eine PDF-Datei in den Storage hoch
   */
  async uploadDocument(
    file: File,
    mandantennummer: string,
    personalnummer: string,
    monat: number,
    jahr: number
  ): Promise<StorageUploadResult> {
    try {
      // Validierung
      if (!file.type || file.type !== 'application/pdf') {
        return {
          success: false,
          error: 'Nur PDF-Dateien sind erlaubt'
        };
      }
      
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > this.MAX_FILE_SIZE_MB) {
        return {
          success: false,
          error: `Datei zu groß (${fileSizeMB.toFixed(2)}MB). Maximum: ${this.MAX_FILE_SIZE_MB}MB`
        };
      }
      
      // Authentifizierung prüfen
      const isAuthenticated = await this.ensureAuthenticated();
      if (!isAuthenticated) {
        return {
          success: false,
          error: 'Supabase-Authentifizierung fehlgeschlagen'
        };
      }
      
      // Generiere eindeutigen Pfad mit neuem Dateinamen-Format (jetzt mit Sanitierung!)
      const storagePath = this.generateStoragePath(
        mandantennummer,
        personalnummer,
        monat,
        jahr,
        file.name
      );
      
      console.log(`[DocumentStorage] Uploading to path: ${storagePath}`);
      
      // Upload zu Supabase Storage
      const { data, error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (error) {
        console.error('Storage upload error:', error);
        return {
          success: false,
          error: `Upload fehlgeschlagen: ${error.message}`
        };
      }
      
      return {
        success: true,
        storagePath: data.path
      };
      
    } catch (error) {
      console.error('Document upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }
  
  /**
   * NEU (Sonderversand): Lädt eine PDF-Datei für den außerplanmäßigen Versand
   * hoch. Der Storage-Pfad enthält KEIN Monat/Jahr, da Sonderversand-Dokumente
   * keinem Monatsschema folgen.
   *
   * Format: sonderversand/{mandant}/{personal}/{timestamp}/{Originalname}.pdf
   */
  async uploadSonderversandDocument(
    file: File,
    mandantennummer: string,
    personalnummer: string
  ): Promise<StorageUploadResult> {
    try {
      // Validierung
      if (!file.type || file.type !== 'application/pdf') {
        return {
          success: false,
          error: 'Nur PDF-Dateien sind erlaubt'
        };
      }
      
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > this.MAX_FILE_SIZE_MB) {
        return {
          success: false,
          error: `Datei zu groß (${fileSizeMB.toFixed(2)}MB). Maximum: ${this.MAX_FILE_SIZE_MB}MB`
        };
      }
      
      // Authentifizierung prüfen
      const isAuthenticated = await this.ensureAuthenticated();
      if (!isAuthenticated) {
        return {
          success: false,
          error: 'Supabase-Authentifizierung fehlgeschlagen'
        };
      }
      
      // Pfad ohne Monat/Jahr. WICHTIG: Der hochgeladene Originalname wird
      // bewusst NICHT umbenannt oder saniert, da der Empfänger die Datei beim
      // Download unter genau diesem Namen erhält. Eindeutigkeit entsteht
      // ausschließlich über einen vorgelagerten Timestamp-Ordner, sodass der
      // Basisname identisch mit dem Originaldateinamen bleibt.
      const timestamp = Date.now();
      const storagePath = `sonderversand/${mandantennummer}/${personalnummer}/${timestamp}/${file.name}`;
      
      console.log(`[DocumentStorage] Uploading Sonderversand to path: ${storagePath}`);
      
      // Upload zu Supabase Storage
      const { data, error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (error) {
        console.error('Storage upload error:', error);
        return {
          success: false,
          error: `Upload fehlgeschlagen: ${error.message}`
        };
      }
      
      return {
        success: true,
        storagePath: data.path
      };
      
    } catch (error) {
      console.error('Sonderversand upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }
  
  /**
   * Erstellt eine signierte URL für temporären Download (18 Monate = 46656000 Sekunden)
   */
  async createSignedDownloadUrl(storagePath: string): Promise<StorageDownloadResult> {
    try {
      const isAuthenticated = await this.ensureAuthenticated();
      if (!isAuthenticated) {
        return {
          success: false,
          error: 'Supabase-Authentifizierung fehlgeschlagen'
        };
      }
      
      // 18 Monate in Sekunden: 18 * 30 * 24 * 60 * 60 = 46656000
      const { data, error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .createSignedUrl(storagePath, 46656000);
      
      if (error) {
        console.error('Signed URL creation error:', error);
        return {
          success: false,
          error: `URL-Erstellung fehlgeschlagen: ${error.message}`
        };
      }
      
      if (!data?.signedUrl) {
        return {
          success: false,
          error: 'Keine signierte URL erhalten'
        };
      }
      
      return {
        success: true,
        downloadUrl: data.signedUrl
      };
      
    } catch (error) {
      console.error('Signed URL creation error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }
  
  /**
   * Löscht ein Dokument aus dem Storage
   */
  async deleteDocument(storagePath: string): Promise<{ success: boolean; error?: string }> {
    try {
      const isAuthenticated = await this.ensureAuthenticated();
      if (!isAuthenticated) {
        return {
          success: false,
          error: 'Supabase-Authentifizierung fehlgeschlagen'
        };
      }
      
      const { error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .remove([storagePath]);
      
      if (error) {
        console.error('Storage delete error:', error);
        return {
          success: false,
          error: `Löschen fehlgeschlagen: ${error.message}`
        };
      }
      
      return { success: true };
      
    } catch (error) {
      console.error('Document delete error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }
  
  /**
   * Prüft ob eine Datei im Storage existiert
   */
  async fileExists(storagePath: string): Promise<boolean> {
    try {
      const isAuthenticated = await this.ensureAuthenticated();
      if (!isAuthenticated) {
        return false;
      }
      
      const { data, error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .list(storagePath.substring(0, storagePath.lastIndexOf('/')));
      
      if (error || !data) {
        return false;
      }
      
      const filename = storagePath.substring(storagePath.lastIndexOf('/') + 1);
      return data.some(file => file.name === filename);
      
    } catch (error) {
      console.error('File exists check error:', error);
      return false;
    }
  }
}

export const documentStorageService = new DocumentStorageService();
